import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../utils/api';
import { Category } from '../../types/category';
import { CATEGORY_OPTIONS } from '../../types/category';

interface CustomFile {
  _id: string;
  createdDate: Date;
  title: string;
  description: string;
  price: number;
  discount: number;
  category: Category;
  imagePath: string;
  downloadPath: string;
  active: boolean;
  priority: number;
  isPromoted: boolean;
}

interface FileFormData {
  title: string;
  description: string;
  price: number;
  discount: number;
  category: Category;
  imagePath: string;
  downloadPath: string;
  active: boolean;
  priority: number;
  isPromoted: boolean;
}

export const FilesManagement: React.FC = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<CustomFile[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [editingFile, setEditingFile] = useState<CustomFile | null>(null);
  const [formData, setFormData] = useState<FileFormData>({
    title: '',
    description: '',
    price: 0,
    discount: 0,
    category: CATEGORY_OPTIONS[0].value,
    imagePath: '',
    downloadPath: '',
    active: true,
    priority: 0,
    isPromoted: false,
  });
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get<{ data: CustomFile[] }>('/files/all');
      setFiles(response.data.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (file?: CustomFile) => {
    if (file) {
      setEditingFile(file);
      setFormData({
        ...file,
        active: file.active ?? true,
        priority: file.priority ?? 0,
        isPromoted: file.isPromoted ?? false,
        discount: file.discount ?? 0,
      });
    } else {
      setEditingFile(null);
      setFormData({
        title: '',
        description: '',
        price: 0,
        discount: 0,
        category: CATEGORY_OPTIONS[0].value,
        imagePath: '',
        downloadPath: '',
        active: true,
        priority: 0,
        isPromoted: false,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingFile
        ? `/files/${editingFile._id}`
        : '/files';
      const method = editingFile ? 'put' : 'post';

      await api[method]<{ data: CustomFile }>(url, formData);
      fetchFiles();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving file:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.files.confirmDelete'))) {
      try {
        await api.delete(`/files/${id}`);
        fetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
  };

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, type: 'image' | 'download') => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'download') => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileUpload = async (file: File, type: 'image' | 'download') => {
    if (!file) return;

    try {
      setIsUploading(true);
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64String = reader.result as string;
        
        try {
          const response = await api.post<{ url: string }>('/files/upload', {
            image: base64String
          });

          if (response.data && response.data.url) {
            if (type === 'image') {
              setFormData(prev => ({ 
                ...prev, 
                imagePath: response.data.url 
              }));
            } else {
              setFormData(prev => ({ 
                ...prev, 
                downloadPath: response.data.url 
              }));
            }
          } else {
            throw new Error('No file URL received from server');
          }
        } catch (error: any) {
          console.error('Error uploading file:', error);
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setIsUploading(false);
      };
    } catch (error) {
      console.error('Error processing file:', error);
      setIsUploading(false);
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{t('admin.tabs.files')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          {t('admin.files.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.files.title')}</TableCell>
              <TableCell>{t('admin.files.category')}</TableCell>
              <TableCell>{t('admin.files.price')}</TableCell>
              <TableCell>{t('admin.files.discount')}</TableCell>
              <TableCell>{t('admin.files.active')}</TableCell>
              <TableCell>{t('admin.files.promoted')}</TableCell>
              <TableCell>{t('admin.files.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((file) => (
                <TableRow key={file._id}>
                  <TableCell>{file.title}</TableCell>
                  <TableCell>{file.category}</TableCell>
                  <TableCell>{file.price}</TableCell>
                  <TableCell>{file.discount}%</TableCell>
                  <TableCell>{file.active ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>{file.isPromoted ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(file)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(file._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={files.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFile ? t('admin.files.edit') : t('admin.files.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.files.title')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('admin.files.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.files.category')}
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  required
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.files.price')}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  required
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.files.discount')}
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) })}
                  required
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.files.priority')}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.files.active')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPromoted}
                      onChange={(e) => setFormData({ ...formData, isPromoted: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.files.promoted')}
                />
              </Grid>
              
              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('admin.files.image')}
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'image')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    onChange={(e) => handleFileSelect(e, 'image')}
                  />
                  <label htmlFor="image-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.files.dragAndDropImage')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.files.orClickToUploadImage')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {formData.imagePath && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.files.selectedImage')}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.imagePath}
                      onChange={(e) => setFormData({ ...formData, imagePath: e.target.value })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Download File Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('admin.files.downloadFile')}
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'download')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    id="download-upload"
                    onChange={(e) => handleFileSelect(e, 'download')}
                  />
                  <label htmlFor="download-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.files.dragAndDropDownload')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.files.orClickToUploadDownload')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {formData.downloadPath && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.files.selectedDownload')}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.downloadPath}
                      onChange={(e) => setFormData({ ...formData, downloadPath: e.target.value })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingFile ? t('common.save') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}; 